import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Paper,
  useTheme
} from '@mui/material';
import {
  AreaChart,
  Area,
  YAxis,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  SwapHoriz,
  AccountBalance,
  Visibility,
  AccessTime
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';
import { track } from '../../../../utils/track';

const INC_TOKEN_ADDRESS = 'Fe5c469ADZyvnZrB8gdDsYt5eLKodaVYhYJCdunVr1nA';

interface TokenData {
  symbol: string;
  name: string;
  price: number;
  priceChange24h: number;
  priceChange1h: number;
  priceChange7d: number;
  volume24h: number;
  marketCap: number;
  liquidity: number;
  fdv: number;
  dexId: string;
  pairAddress: string;
  lastUpdated: string;
}

interface PriceData {
  timestamp: number;
  price: number;
  volume: number;
  date: string;
}

export const TokenGraph = () => {
  const [tokenData, setTokenData] = useState<TokenData | null>(null);
  const [priceData, setPriceData] = useState<PriceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState<string>('24h');
  const [chartType, setChartType] = useState<'line' | 'area'>('area');
  const theme = useTheme();

  const timeframeOptions = [
    { value: '1h', label: '1H' },
    { value: '4h', label: '4H' },
    { value: '24h', label: '24H' },
    { value: '7d', label: '7D' },
    { value: '30d', label: '30D' }
  ];

  const fetchTokenData = async (selectedTimeframe: string) => {
    try {
      setLoading(true);
      setError(null);

      // Fetch current token data from DexScreener
      const response = await axios.get(
        `https://api.dexscreener.com/latest/dex/tokens/${INC_TOKEN_ADDRESS}`
      );

      if (!response.data?.pairs || response.data.pairs.length === 0) {
        throw new Error('No trading pairs found for INC token');
      }

      // Find the most liquid pair
      const mostLiquidPair = response.data.pairs.reduce(
        (prev: any, current: any) => {
          const prevLiquidity = parseFloat(prev.liquidity?.usd || '0');
          const currentLiquidity = parseFloat(current.liquidity?.usd || '0');
          return currentLiquidity > prevLiquidity ? current : prev;
        }
      );

      const tokenInfo: TokenData = {
        symbol: mostLiquidPair.baseToken?.symbol || 'INC',
        name: mostLiquidPair.baseToken?.name || 'Incrypt Token',
        price: parseFloat(mostLiquidPair.priceUsd || '0'),
        priceChange24h: parseFloat(mostLiquidPair.priceChange?.h24 || '0'),
        priceChange1h: parseFloat(mostLiquidPair.priceChange?.h1 || '0'),
        priceChange7d: parseFloat(mostLiquidPair.priceChange?.h6 || '0'),
        volume24h: parseFloat(mostLiquidPair.volume?.h24 || '0'),
        marketCap: parseFloat(mostLiquidPair.marketCap || '0'),
        liquidity: parseFloat(mostLiquidPair.liquidity?.usd || '0'),
        fdv: parseFloat(mostLiquidPair.fdv || '0'),
        dexId: mostLiquidPair.dexId || '',
        pairAddress: mostLiquidPair.pairAddress || '',
        lastUpdated: new Date().toLocaleString()
      };

      setTokenData(tokenInfo);

      // Generate realistic price history based on current price
      const currentPrice = tokenInfo.price;
      const currentTimestamp = Date.now();
      const dataPoints = getDataPointsForTimeframe(selectedTimeframe);
      const sampleData: PriceData[] = [];

      for (let i = 0; i < dataPoints; i++) {
        const timestamp =
          currentTimestamp -
          (dataPoints - 1 - i) * getIntervalForTimeframe(selectedTimeframe);
        // Add some realistic price variation based on 24h change
        const baseVariation = (tokenInfo.priceChange24h / 100) * 0.1; // 10% of the 24h change
        const randomVariation = (Math.random() - 0.5) * 0.05; // ±2.5% random
        const totalVariation = baseVariation + randomVariation;
        const price = currentPrice * (1 + totalVariation * Math.sin(i * 0.1));
        const volume = Math.random() * tokenInfo.volume24h * 0.1; // Random volume up to 10% of daily

        sampleData.push({
          timestamp,
          price: Math.max(0, price),
          volume,
          date: format(new Date(timestamp), getDateFormat(selectedTimeframe))
        });
      }

      // Add current data point
      sampleData.push({
        timestamp: currentTimestamp,
        price: currentPrice,
        volume: tokenInfo.volume24h,
        date: format(
          new Date(currentTimestamp),
          getDateFormat(selectedTimeframe)
        )
      });

      setPriceData(sampleData);
    } catch (err: any) {
      console.error('Error fetching token data:', err);
      setError(err.message || 'Failed to load token data');
    } finally {
      setLoading(false);
    }
  };

  const getDataPointsForTimeframe = (tf: string): number => {
    switch (tf) {
      case '1h':
        return 60;
      case '4h':
        return 48;
      case '24h':
        return 24;
      case '7d':
        return 168;
      case '30d':
        return 30;
      default:
        return 24;
    }
  };

  const getIntervalForTimeframe = (tf: string): number => {
    switch (tf) {
      case '1h':
        return 60 * 1000;
      case '4h':
        return 5 * 60 * 1000;
      case '24h':
        return 60 * 60 * 1000;
      case '7d':
        return 60 * 60 * 1000;
      case '30d':
        return 24 * 60 * 60 * 1000;
      default:
        return 60 * 60 * 1000;
    }
  };

  const getDateFormat = (tf: string): string => {
    switch (tf) {
      case '1h':
      case '4h':
        return 'HH:mm';
      case '24h':
        return 'HH:mm';
      case '7d':
        return 'MMM dd, HH:mm';
      case '30d':
        return 'MMM dd';
      default:
        return 'MMM dd, HH:mm';
    }
  };

  useEffect(() => {
    fetchTokenData(timeframe);
  }, [timeframe]);

  useEffect(() => {
    fetchTokenData('24h');

    // Update every 30 seconds
    const interval = setInterval(() => fetchTokenData(timeframe), 30000);

    return () => clearInterval(interval);
  }, []);

  const handleTimeframeChange = (
    event: React.MouseEvent<HTMLElement>,
    newTimeframe: string
  ) => {
    if (newTimeframe !== null) {
      setTimeframe(newTimeframe);
      track('token.chart_timeframe', { timeframe: newTimeframe });
    }
  };

  const handleChartTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newChartType: 'line' | 'area'
  ) => {
    if (newChartType !== null) {
      setChartType(newChartType);
      track('token.chart_type', { type: newChartType });
    }
  };

  const formatPrice = (price: number) => {
    if (price < 0.001) {
      return price.toExponential(3);
    }
    return `$${price.toFixed(6)}`;
  };

  const formatLargeNumber = (num: number) => {
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    return num.toFixed(2);
  };

  const getPriceChangeColor = (change: number) => {
    if (change > 0) return theme.palette.success.main;
    if (change < 0) return theme.palette.error.main;
    return theme.palette.text.secondary;
  };

  const getPriceChangeIcon = (change: number) => {
    if (change > 0) return <TrendingUp />;
    if (change < 0) return <TrendingDown />;
    return <TrendingFlat />;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          sx={{
            p: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            border: '1px solid #333',
            borderRadius: 1
          }}
        >
          <Typography variant="body2" color="white">
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color }}>
              {`Price: ${formatPrice(entry.value)}`}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box
        sx={{
          py: '2rem',
          width: '100%',
          maxWidth: '900px',
          mx: 'auto',
          textAlign: 'center'
        }}
      >
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 4 }}>
          INC Token Stats
        </Typography>
        <CircularProgress size={60} sx={{ color: '#38e8ff' }} />
        <Typography sx={{ mt: 2 }}>Loading token data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          py: '2rem',
          width: '100%',
          maxWidth: '900px',
          mx: 'auto',
          textAlign: 'center'
        }}
      >
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 4 }}>
          INC Token Stats
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Please check your internet connection and try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: '2rem', width: '100%', maxWidth: '900px', mx: 'auto' }}>
      <Typography
        variant="h4"
        textAlign="center"
        fontWeight="bold"
        sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }, mb: 4 }}
      >
        INC Token Stats
      </Typography>

      {/* Main Price Display */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          background:
            'linear-gradient(180deg, rgba(11,17,25,0.92), rgba(7,11,17,0.96))',
          border: '1px solid rgba(56,232,255,0.22)',
          borderRadius: '16px',
          color: 'white'
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" fontWeight="bold">
              {tokenData?.symbol}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.8 }}>
              {tokenData?.name}
            </Typography>
            <Typography variant="h3" fontWeight="bold" sx={{ mt: 1 }}>
              {tokenData ? formatPrice(tokenData.price) : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" flexDirection="column" alignItems="flex-end">
              <Box display="flex" alignItems="center" mb={1}>
                {tokenData && getPriceChangeIcon(tokenData.priceChange24h)}
                <Typography
                  variant="h6"
                  sx={{
                    ml: 1,
                    color: tokenData
                      ? getPriceChangeColor(tokenData.priceChange24h)
                      : 'white'
                  }}
                >
                  {tokenData
                    ? `${tokenData.priceChange24h > 0 ? '+' : ''}${tokenData.priceChange24h.toFixed(2)}%`
                    : 'N/A'}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                24h Change
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Token Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <SwapHoriz color="primary" />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  24h Volume
                </Typography>
              </Box>
              <Typography variant="h6" color="white" fontWeight="bold">
                ${tokenData ? formatLargeNumber(tokenData.volume24h) : 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <AccountBalance color="primary" />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  Market Cap
                </Typography>
              </Box>
              <Typography variant="h6" color="white" fontWeight="bold">
                ${tokenData ? formatLargeNumber(tokenData.marketCap) : 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Visibility color="primary" />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  Liquidity
                </Typography>
              </Box>
              <Typography variant="h6" color="white" fontWeight="bold">
                ${tokenData ? formatLargeNumber(tokenData.liquidity) : 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <AccessTime color="primary" />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  Last Updated
                </Typography>
              </Box>
              <Typography variant="body2" color="white">
                {tokenData ? tokenData.lastUpdated : 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Chart Controls */}
      {/* <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <ToggleButtonGroup
          value={timeframe}
          exclusive
          onChange={handleTimeframeChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white'
              }
            }
          }}
        >
          {timeframeOptions.map((option) => (
            <ToggleButton key={option.value} value={option.value}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={chartType}
          exclusive
          onChange={handleChartTypeChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white'
              }
            }
          }}
        >
          <ToggleButton value="line">Line</ToggleButton>
          <ToggleButton value="area">Area</ToggleButton>
        </ToggleButtonGroup>
      </Box> */}

      {/* Interactive Price Chart */}
      {/* <Box sx={{ position: 'relative', width: '100%', minHeight: '400px', mt: 2 }}>
        <Typography variant="h6" textAlign="center" sx={{ mb: 2, color: 'white' }}>
          Interactive Price Chart ({timeframe.toUpperCase()})
        </Typography>
        <ResponsiveContainer width="100%" height={400}>
          {chartType === 'area' ? (
            <AreaChart data={priceData}>
              <defs>
                <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#38e8ff" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#38e8ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
              <XAxis
                dataKey="date"
                stroke="rgba(255, 255, 255, 0.7)"
                fontSize={12}
                tickLine={false}
              />
              <YAxis
                stroke="rgba(255, 255, 255, 0.7)"
                fontSize={12}
                tickLine={false}
                tickFormatter={(value) => `$${value.toFixed(6)}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="price"
                stroke="#38e8ff"
                fillOpacity={1}
                fill="url(#colorPrice)"
                strokeWidth={2}
              />
            </AreaChart>
          ) : (
            <LineChart data={priceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
              <XAxis
                dataKey="date"
                stroke="rgba(255, 255, 255, 0.7)"
                fontSize={12}
                tickLine={false}
              />
              <YAxis
                stroke="rgba(255, 255, 255, 0.7)"
                fontSize={12}
                tickLine={false}
                tickFormatter={(value) => `$${value.toFixed(6)}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="price"
                stroke="#38e8ff"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </Box> */}

      {/* DEX Info */}
      {/* <Box sx={{ mt: 3 }}>
        <Paper elevation={1} sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.03)' }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Trading Data from {tokenData?.dexId.toUpperCase() || 'DEX'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Contract: {INC_TOKEN_ADDRESS}
          </Typography>
        </Paper>
      </Box>  */}
    </Box>
  );
};
